import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CUSTOMER_KINDS, CUSTOMER_LOCALES, FORMS_OF_ADDRESS, SALUTATIONS, customerSchema } from "@jornado/shared";
import { api, fehlertext } from "./api";
import {
  ArchivMarke, Auswahl, Eingabe, Feld, Fehler, Karte, Knopf, Laedt, Leer, Seitenkopf, Textfeld
} from "./ui";

export interface KundeZeile {
  id: string;
  display_name: string;
  company_name: string | null;
  kind: string;
  language: string;
  email: string | null;
  phone: string | null;
  billing_city: string | null;
  archived_at: string | null;
  property_count: number;
}

export interface KundeVoll {
  id: string;
  kind: "private" | "company";
  display_name: string;
  company_name: string | null;
  tax_id: string | null;
  form_of_address: "du" | "sie";
  salutation: "herr" | "frau" | "familie" | "neutral";
  salutation_name: string | null;
  language: "de" | "en";
  email: string | null;
  phone: string | null;
  whatsapp: string | null;
  billing_street: string | null;
  billing_postal_code: string | null;
  billing_city: string | null;
  billing_country: string | null;
  notes: string | null;
  archived_at: string | null;
}

interface ObjektKurz {
  id: string;
  name: string;
  city: string | null;
  municipality: string | null;
  service_level: string | null;
  archived_at: string | null;
}

/** Formularzustand. Alles Text, weil ein Formularfeld nun einmal Text liefert. */
type Formular = Record<string, string>;

const LEER: Formular = {
  kind: "private", displayName: "", companyName: "", taxId: "", formOfAddress: "sie",
  salutation: "neutral", salutationName: "",
  language: "de", email: "", phone: "", whatsapp: "", billingStreet: "",
  billingPostalCode: "", billingCity: "", billingCountry: "DE", notes: ""
};

// --------------------------------------------------------------------- Liste

interface OhneAnrede {
  id: string;
  anzeigename: string;
  firma: string | null;
  art: string;
  anrede: string;
  vorschlag: string | null;
}

export function KundenListe({ darfAendern }: { darfAendern: boolean }) {
  const { t } = useTranslation();
  const [kunden, setKunden] = useState<KundeZeile[] | null>(null);
  const [suche, setSuche] = useState("");
  const [mitArchiv, setMitArchiv] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [ohneAnrede, setOhneAnrede] = useState<OhneAnrede[]>([]);
  const [helfer, setHelfer] = useState(false);
  /** Angehakte Kunden fuer die Sammelaktion. */
  const [auswahl, setAuswahl] = useState<string[]>([]);
  /** Zaehler, der ein Neuladen ausloest, wenn der Helfer etwas uebernommen hat. */
  const [stand, setStand] = useState(0);

  useEffect(() => {
    // Kurz warten, damit nicht jeder Tastendruck eine Anfrage ausloest.
    const timer = setTimeout(() => {
      const p = new URLSearchParams();
      if (suche.trim()) p.set("q", suche.trim());
      if (mitArchiv) p.set("archiviert", "ja");
      api
        .get<{ customers: KundeZeile[] }>(`/customers?${p}`)
        .then((r) => { setKunden(r.customers); setFehler(null); })
        .catch((e) => setFehler(fehlertext(e, t("fehler.laden"))));
    }, 250);
    return () => clearTimeout(timer);
  }, [suche, mitArchiv, t, stand]);

  /*
   * Wie viele Kunden noch ohne Anrede sind, steht nicht in der Kundenliste
   * daneben: es waere in jeder Zeile dieselbe Auskunft. Die Zahl steht einmal
   * ueber der Liste, und zwar nur dann, wenn sie groesser als null ist.
   */
  useEffect(() => {
    void api.get<{ kunden: OhneAnrede[] }>("/customers/anrede-vorschlaege")
      .then((r) => setOhneAnrede(r.kunden))
      .catch(() => setOhneAnrede([]));
  }, [stand]);

  /*
   * Die Auswahl gilt nur fuer das, was gerade in der Liste steht. Wer sucht
   * oder das Archiv ein- und ausblendet, bekommt eine andere Liste, und eine
   * Auswahl, die unsichtbare Kunden mitschleppt, ist genau die Art Falle, die
   * beim Archivieren teuer wird.
   */
  const gewaehlte = auswahl.filter((id) => (kunden ?? []).some((k) => k.id === id));

  function umschalten(id: string) {
    setAuswahl((alt) => (alt.includes(id) ? alt.filter((x) => x !== id) : [...alt, id]));
  }

  return (
    <>
      <Seitenkopf
        titel={t("kunden.titel")}
        hinweis={kunden ? t("kunden.anzahl", { count: kunden.length }) : undefined}
        aktionen={
          <div className="flex flex-wrap items-center gap-4">
            {/*
              Der einzige Weg zur Objektliste, seit sie keinen eigenen Reiter
              mehr hat. Gebraucht wird sie fuer den Koordinatenhelfer und den
              Filter nach fehlenden Zugangsdaten; die einzelne Objektakte
              erreicht man ueber den Kunden.
            */}
            <Link to="/objekte" className="text-sm text-leise underline">
              {t("objekte.alle")}
            </Link>
            {darfAendern && <Link to="/kunden/neu"><Knopf>{t("kunden.neu")}</Knopf></Link>}
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-4">
        <Eingabe
          type="search" value={suche} onChange={(e) => setSuche(e.target.value)}
          placeholder={t("kunden.suche")} aria-label={t("kunden.suche")}
        />
        <label className="flex shrink-0 items-center gap-2 text-sm text-leise">
          <input type="checkbox" checked={mitArchiv} onChange={(e) => setMitArchiv(e.target.checked)} />
          {t("archiv.zeigen")}
        </label>
      </div>

      {darfAendern && gewaehlte.length > 0 && (
        <Sammelaktion
          kunden={(kunden ?? []).filter((k) => gewaehlte.includes(k.id))}
          onFertig={(fehlgeschlagen) => {
            setAuswahl(fehlgeschlagen);
            setStand((x) => x + 1);
          }}
          onAbwaehlen={() => setAuswahl([])}
        />
      )}

      <Fehler text={fehler} />

      {helfer ? (
        <Anredehelfer
          kunden={ohneAnrede}
          onFertig={() => { setHelfer(false); setStand((x) => x + 1); }}
        />
      ) : ohneAnrede.length > 0 && darfAendern ? (
        <div className="mb-4">
          <Knopf art="neben" onClick={() => setHelfer(true)}>
            {t("kunden.anredeHelferStarten", { count: ohneAnrede.length })}
          </Knopf>
        </div>
      ) : null}

      {!kunden ? <Laedt /> : kunden.length === 0 ? <Leer text={t("kunden.leer")} /> : (
        <ul className="space-y-2">
          {kunden.map((k) => (
            <li key={k.id} className="flex items-start gap-2">
              {/*
                Das Kaestchen steht ausserhalb des Verweises. Innerhalb waere
                jeder Haken zugleich ein Klick auf die Kundenakte, und wer drei
                Kunden auswaehlen will, landet dreimal auf einer anderen Seite.
              */}
              {darfAendern && (
                <label className="mt-4 flex cursor-pointer items-center p-1">
                  <input
                    type="checkbox"
                    className="h-5 w-5"
                    checked={gewaehlte.includes(k.id)}
                    onChange={() => umschalten(k.id)}
                    aria-label={k.display_name}
                  />
                </label>
              )}
              <Link to={`/kunden/${k.id}`} className="block flex-1 rounded-card bg-white p-4 transition hover:bg-marke/10">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">{k.display_name}</span>
                  {/* Bei Firmen ist der Firmenname oft derselbe wie der Anzeigename. Ihn
                      dann ein zweites Mal danebenzuschreiben sieht nach einem Fehler aus. */}
                  {k.company_name && k.company_name !== k.display_name && (
                    <span className="text-sm text-leise">{k.company_name}</span>
                  )}
                  {k.archived_at && <ArchivMarke text={t("archiv.marke")} />}
                </div>
                <div className="mt-1 text-sm text-leise">
                  {[k.email, k.phone, k.billing_city].filter(Boolean).join(" · ") || "—"}
                  {" · "}
                  {t("kunden.objekte", { count: k.property_count })}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/**
 * Mehrere Kunden auf einmal archivieren oder zurueckholen.
 *
 * Der Anlass ist die Uebernahme aus der Altanwendung: dort stehen Karteileichen
 * und Testkunden, und jede einzeln aufzurufen, zu archivieren und zurueck zur
 * Liste zu gehen ist derselbe Weg fuenfzehnmal.
 *
 * **Die Objekte gehen mit**, wie beim einzelnen Kunden auch. Deshalb steht in
 * der Nachfrage nicht nur die Zahl der Kunden, sondern auch die der Objekte:
 * fuenf Kunden koennen sieben Haeuser aus dem Dienstplan nehmen, und das soll
 * niemand erst hinterher merken.
 *
 * Gesendet wird nacheinander und ueber dieselbe Schnittstelle wie beim
 * einzelnen Kunden. Ein eigener Sammelaufruf waere schneller und haette eine
 * zweite Fassung derselben Regeln; scheitert einer, bleiben die uebrigen
 * angehakt und der Grund steht daneben.
 */
function Sammelaktion({
  kunden, onFertig, onAbwaehlen
}: {
  kunden: KundeZeile[];
  /** Bekommt die Kennungen, bei denen es nicht geklappt hat. */
  onFertig: (fehlgeschlagen: string[]) => void;
  onAbwaehlen: () => void;
}) {
  const { t } = useTranslation();
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  /*
   * Was die Aktion tut, richtet sich danach, was ausgewaehlt ist. Sind alle
   * archiviert, ist es ein Zurueckholen; sonst ein Archivieren. Zwei Knoepfe
   * nebeneinander waeren zwei Gelegenheiten, den falschen zu treffen.
   */
  const alleArchiviert = kunden.every((k) => k.archived_at);
  const objekte = kunden.reduce((n, k) => n + (k.archived_at ? 0 : k.property_count), 0);

  async function ausfuehren() {
    const frage = alleArchiviert
      ? t("kunden.sammelZurueckFrage", { count: kunden.length })
      : t("kunden.sammelArchivFrage", { count: kunden.length, objekte });
    if (!window.confirm(frage)) return;

    setLaeuft(true);
    setFehler(null);
    const gescheitert: string[] = [];
    let letzterGrund: string | null = null;

    for (const k of kunden) {
      try {
        await api.post(`/customers/${k.id}/${alleArchiviert ? "restore" : "archive"}`);
      } catch (e) {
        gescheitert.push(k.id);
        letzterGrund = `${k.display_name}: ${fehlertext(e)}`;
      }
    }

    setLaeuft(false);
    setFehler(letzterGrund);
    onFertig(gescheitert);
  }

  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-card border-l-4 border-marke bg-white p-3">
      <span className="text-sm font-semibold">
        {t("kunden.ausgewaehlt", { count: kunden.length })}
      </span>
      <Knopf art="warnung" disabled={laeuft} onClick={() => void ausfuehren()}>
        {alleArchiviert ? t("archiv.zurueckholen") : t("archiv.archivieren")}
      </Knopf>
      <Knopf art="neben" disabled={laeuft} onClick={onAbwaehlen}>
        {t("kunden.auswahlAufheben")}
      </Knopf>
      {fehler && <span className="text-sm text-red-700">{fehler}</span>}
    </div>
  );
}

/**
 * Die Anrede der Reihe nach nachtragen.
 *
 * Bei der Uebernahme aus der Altanwendung gab es je Kunde nur einen Namen.
 * Fuer den Kalender und fuer den Brief braucht es zwei Angaben mehr, und die
 * fuenfundvierzig Mal einzeln aufzurufen, zu bearbeiten und zu speichern
 * kostet einen Abend.
 *
 * Deshalb dieselbe Bauart wie beim Koordinatenhelfer: alles untereinander,
 * ein Vorschlag je Zeile, uebernommen wird per Klick. Vorgeschlagen ist das
 * letzte Wort des Anzeigenamens, denn dort steht der Nachname meistens. Bei
 * Firmen steht dort nichts, weil sich aus "Ejemplo Living S.L." keiner
 * ableiten laesst.
 *
 * Die Anrede selbst wird nie geraten. Ob jemand Herr oder Frau ist, steht in
 * keinem Nachnamen, und ein falsch angesprochener Eigentuemer merkt es
 * sofort. Sie steht als Auswahl daneben und bleibt auf neutral, wenn niemand
 * sie setzt: dann steht im Kalender der blosse Name, und das ist nie falsch.
 */
function Anredehelfer({ kunden, onFertig }: { kunden: OhneAnrede[]; onFertig: () => void }) {
  const { t } = useTranslation();
  const [namen, setNamen] = useState<Record<string, string>>(() =>
    Object.fromEntries(kunden.map((k) => [k.id, k.vorschlag ?? ""]))
  );
  const [anreden, setAnreden] = useState<Record<string, string>>(() =>
    Object.fromEntries(kunden.map((k) => [k.id, k.anrede]))
  );
  const [fertig, setFertig] = useState<string[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);

  async function uebernehmen(k: OhneAnrede) {
    const name = (namen[k.id] ?? "").trim();
    // Ein leeres Feld ist kein Nachname. Lieber nichts speichern als ein
    // Leerzeichen, das spaeter als "gesetzt" gilt.
    if (!name) return;
    setFehler(null);
    try {
      await api.patch(`/customers/${k.id}`, {
        salutation: anreden[k.id],
        salutationName: name
      });
      setFertig((alt) => [...alt, k.id]);
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    }
  }

  return (
    <Karte className="mb-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-leise">
          {t("kunden.anredeHelferStand", { fertig: fertig.length, gesamt: kunden.length })}
        </p>
        <Knopf art="neben" onClick={onFertig}>{t("kunden.anredeHelferBeenden")}</Knopf>
      </div>
      <p className="mt-1 text-sm text-leise">{t("kunden.anredeHelferHinweis")}</p>
      <Fehler text={fehler} />

      <ul className="mt-3 space-y-2">
        {kunden.map((k) => {
          const erledigt = fertig.includes(k.id);
          return (
            <li key={k.id} className="rounded border border-linie p-3">
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="font-semibold">{k.anzeigename}</span>
                {k.firma && k.firma !== k.anzeigename && (
                  <span className="text-sm text-leise">{k.firma}</span>
                )}
              </div>

              {erledigt ? (
                <p className="mt-1 text-sm font-semibold text-verweis">
                  {t("kunden.anredeUebernommen")}
                </p>
              ) : (
                <>
                  <div className="mt-2 flex flex-wrap items-end gap-2">
                    <Feld label={t("kunden.briefanrede")}>
                      <Auswahl
                        value={anreden[k.id] ?? "neutral"}
                        onChange={(e) =>
                          setAnreden((alt) => ({ ...alt, [k.id]: e.target.value }))
                        }
                      >
                        {SALUTATIONS.map((a) => (
                          <option key={a} value={a}>{t(`kunden.salutation.${a}`)}</option>
                        ))}
                      </Auswahl>
                    </Feld>
                    <Feld label={t("kunden.anredename")}>
                      <Eingabe
                        value={namen[k.id] ?? ""}
                        onChange={(e) => setNamen((alt) => ({ ...alt, [k.id]: e.target.value }))}
                        // Mit der Eingabetaste eine Zeile weiter: wer
                        // fuenfundvierzig Zeilen abarbeitet, will nicht
                        // fuenfundvierzig Mal zur Maus greifen.
                        onKeyDown={(e) => { if (e.key === "Enter") void uebernehmen(k); }}
                      />
                    </Feld>
                    <div className="pb-1">
                      <Knopf disabled={!(namen[k.id] ?? "").trim()} onClick={() => void uebernehmen(k)}>
                        {t("aktion.speichern")}
                      </Knopf>
                    </div>
                  </div>
                  {!k.vorschlag && (
                    <p className="mt-1 text-xs text-leise">
                      {t("kunden.anredeKeinVorschlag")}
                    </p>
                  )}
                </>
              )}
            </li>
          );
        })}
      </ul>
    </Karte>
  );
}

// --------------------------------------------------------------------- Detail

export function KundeSeite({ darfAendern }: { darfAendern: boolean }) {
  const { t } = useTranslation();
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [kunde, setKunde] = useState<KundeVoll | null>(null);
  const [objekte, setObjekte] = useState<ObjektKurz[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [bearbeiten, setBearbeiten] = useState(false);

  function laden() {
    api
      .get<{ customer: KundeVoll; properties: ObjektKurz[] }>(`/customers/${id}`)
      .then((r) => {
        setKunde(r.customer); setObjekte(r.properties); setFehler(null);
      })
      .catch((e) => setFehler(fehlertext(e, t("fehler.laden"))));
  }
  useEffect(laden, [id]);

  async function archivUmschalten() {
    if (!kunde) return;
    /*
     * Nachfragen, solange noch Objekte am Kunden haengen.
     *
     * Sie werden mitarchiviert, und das ist auch so gewollt. Ein Klick, der
     * ausser dem Kunden noch drei Haeuser aus dem Dienstplan nimmt, soll aber
     * nicht unbemerkt passieren.
     */
    const aktive = objekte.filter((o) => !o.archived_at).length;
    if (!kunde.archived_at && aktive > 0 && !window.confirm(t("archiv.kundeMitObjekten", { count: aktive }))) {
      return;
    }
    try {
      await api.post(`/customers/${id}/${kunde.archived_at ? "restore" : "archive"}`);
      laden();
    } catch (e) {
      setFehler(fehlertext(e));
    }
  }

  if (fehler && !kunde) return <Fehler text={fehler} />;
  if (!kunde) return <Laedt />;

  if (bearbeiten) {
    return (
      <KundenFormular
        titel={t("kunden.bearbeiten")}
        werte={ausKunde(kunde)}
        onAbbruch={() => setBearbeiten(false)}
        onSpeichern={async (daten) => {
          await api.patch(`/customers/${id}`, daten);
          setBearbeiten(false);
          laden();
        }}
      />
    );
  }

  return (
    <>
      <Seitenkopf
        titel={kunde.display_name}
        hinweis={kunde.company_name && kunde.company_name !== kunde.display_name
          ? kunde.company_name
          : undefined}
        aktionen={darfAendern && (
          <>
            <Knopf art="neben" onClick={() => setBearbeiten(true)}>{t("aktion.bearbeiten")}</Knopf>
            <Knopf art="warnung" onClick={archivUmschalten}>
              {kunde.archived_at ? t("archiv.zurueckholen") : t("archiv.archivieren")}
            </Knopf>
          </>
        )}
      />
      <Fehler text={fehler} />
      {kunde.archived_at && <div className="mb-4"><ArchivMarke text={t("archiv.marke")} /></div>}

      <div className="grid gap-4 lg:grid-cols-2">
        <Karte>
          <h2 className="mb-3 font-bold">{t("kunden.kontakt")}</h2>
          <Zeile label={t("kunden.art")} wert={t(`kunden.art.${kunde.kind}`)} />
          <Zeile label={t("kunden.anrede")} wert={t(`kunden.anrede.${kunde.form_of_address}`)} />
          <Zeile
            label={t("kunden.briefanrede")}
            wert={briefanrede(kunde, t)}
          />
          <Zeile label={t("kunden.sprache")} wert={kunde.language.toUpperCase()} />
          <Zeile label={t("kunden.email")} wert={kunde.email} />
          <Zeile label={t("kunden.telefon")} wert={kunde.phone} />
          <Zeile label="WhatsApp" wert={kunde.whatsapp} />
          <Zeile label={t("kunden.steuernummer")} wert={kunde.tax_id} />
        </Karte>

        <Karte>
          <h2 className="mb-3 font-bold">{t("kunden.rechnungsadresse")}</h2>
          <Zeile label={t("adresse.strasse")} wert={kunde.billing_street} />
          <Zeile label={t("adresse.plz")} wert={kunde.billing_postal_code} />
          <Zeile label={t("adresse.ort")} wert={kunde.billing_city} />
          <Zeile label={t("adresse.land")} wert={kunde.billing_country} />
        </Karte>

        {kunde.notes && (
          <Karte className="lg:col-span-2">
            <h2 className="mb-3 font-bold">{t("kunden.notizen")}</h2>
            <p className="whitespace-pre-wrap text-sm">{kunde.notes}</p>
          </Karte>
        )}

        <Karte className="lg:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-bold">{t("objekte.titel")}</h2>
            {darfAendern && (
              <Knopf art="neben" onClick={() => navigate(`/objekte/neu?kunde=${id}`)}>
                {t("objekte.neu")}
              </Knopf>
            )}
          </div>
          {objekte.length === 0 ? (
            <p className="text-sm text-leise">{t("objekte.leerBeiKunde")}</p>
          ) : (
            <ul className="space-y-2">
              {objekte.map((o) => (
                <li key={o.id}>
                  <Link to={`/objekte/${o.id}`} className="flex flex-wrap items-center gap-2 rounded px-2 py-2 hover:bg-grund">
                    <span className="font-semibold">{o.name}</span>
                    <span className="text-sm text-leise">{o.service_level}</span>
                    {o.archived_at && <ArchivMarke text={t("archiv.marke")} />}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Karte>
      </div>
    </>
  );
}

function Zeile({ label, wert }: { label: string; wert: string | null | undefined }) {
  return (
    <div className="flex gap-3 border-b border-linie py-1.5 last:border-0">
      <span className="w-40 shrink-0 text-sm text-leise">{label}</span>
      <span className="text-sm [overflow-wrap:anywhere]">{wert || "—"}</span>
    </div>
  );
}

// ------------------------------------------------------------------ Formular

export function KundeNeu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <KundenFormular
      titel={t("kunden.neu")}
      werte={LEER}
      onAbbruch={() => navigate("/kunden")}
      onSpeichern={async (daten) => {
        const r = await api.post<{ id: string }>("/customers", daten);
        navigate(`/kunden/${r.id}`);
      }}
    />
  );
}

function ausKunde(k: KundeVoll): Formular {
  return {
    kind: k.kind, displayName: k.display_name, companyName: k.company_name ?? "",
    taxId: k.tax_id ?? "", formOfAddress: k.form_of_address, language: k.language,
    salutation: k.salutation ?? "neutral", salutationName: k.salutation_name ?? "",
    email: k.email ?? "", phone: k.phone ?? "", whatsapp: k.whatsapp ?? "",
    billingStreet: k.billing_street ?? "", billingPostalCode: k.billing_postal_code ?? "",
    billingCity: k.billing_city ?? "", billingCountry: k.billing_country ?? "",
    notes: k.notes ?? ""
  };
}

function KundenFormular({
  titel, werte, onSpeichern, onAbbruch
}: {
  titel: string;
  werte: Formular;
  onSpeichern: (daten: unknown) => Promise<void>;
  onAbbruch: () => void;
}) {
  const { t } = useTranslation();
  const [f, setF] = useState<Formular>(werte);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const setzen = (k: string) => (e: { target: { value: string } }) =>
    setF((alt) => ({ ...alt, [k]: e.target.value }));

  async function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    // Dasselbe Schema wie im Backend. Der Fehler faellt hier auf, ohne Umweg
    // ueber das Netz, und die Regel steht trotzdem nur an einer Stelle.
    const geprueft = customerSchema.safeParse(f);
    if (!geprueft.success) {
      setFehler(geprueft.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`).join(", "));
      return;
    }
    setLaeuft(true);
    try {
      await onSpeichern(geprueft.data);
    } catch (err) {
      setFehler(fehlertext(err, t("fehler.speichern")));
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <form onSubmit={absenden}>
      <Seitenkopf titel={titel} />
      <Fehler text={fehler} />
      <Karte>
        <div className="grid gap-4 sm:grid-cols-2">
          <Feld label={t("kunden.art")}>
            <Auswahl value={f.kind} onChange={setzen("kind")}>
              {CUSTOMER_KINDS.map((k) => <option key={k} value={k}>{t(`kunden.art.${k}`)}</option>)}
            </Auswahl>
          </Feld>
          <Feld label={t("kunden.name")} hinweis={t("kunden.nameHinweis")}>
            <Eingabe required value={f.displayName} onChange={setzen("displayName")} />
          </Feld>
          <Feld label={t("kunden.firma")}>
            <Eingabe value={f.companyName} onChange={setzen("companyName")} />
          </Feld>
          <Feld label={t("kunden.steuernummer")}>
            <Eingabe value={f.taxId} onChange={setzen("taxId")} />
          </Feld>
          <Feld label={t("kunden.anrede")}>
            <Auswahl value={f.formOfAddress} onChange={setzen("formOfAddress")}>
              {FORMS_OF_ADDRESS.map((a) => <option key={a} value={a}>{t(`kunden.anrede.${a}`)}</option>)}
            </Auswahl>
          </Feld>
          <Feld label={t("kunden.briefanrede")} hinweis={t("kunden.briefanredeHinweis")}>
            <Auswahl value={f.salutation} onChange={setzen("salutation")}>
              {SALUTATIONS.map((a) => <option key={a} value={a}>{t(`kunden.salutation.${a}`)}</option>)}
            </Auswahl>
          </Feld>
          <Feld label={t("kunden.anredename")} hinweis={t("kunden.anredenameHinweis")}>
            <Eingabe value={f.salutationName} onChange={setzen("salutationName")}
              placeholder={f.formOfAddress === "du" ? "Max" : "Meier"} />
          </Feld>
          <Feld label={t("kunden.sprache")} hinweis={t("kunden.spracheHinweis")}>
            <Auswahl value={f.language} onChange={setzen("language")}>
              {CUSTOMER_LOCALES.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
            </Auswahl>
          </Feld>
          {/*
            * Bewusst kein type="email": der Browser laesst dort genau eine
            * Adresse zu und blockiert das Formular, sobald zwei Eigentuemer
            * dastehen. Geprueft wird ohnehin auf dem Server, und dort sind
            * mehrere erlaubt.
            */}
          <Feld label={t("kunden.email")} hinweis={t("kunden.emailHinweis")}>
            <Eingabe type="text" inputMode="email" value={f.email} onChange={setzen("email")} />
          </Feld>
          <Feld label={t("kunden.telefon")}>
            <Eingabe value={f.phone} onChange={setzen("phone")} />
          </Feld>
          <Feld label="WhatsApp">
            <Eingabe value={f.whatsapp} onChange={setzen("whatsapp")} />
          </Feld>
        </div>

        <h2 className="mb-3 mt-8 font-bold">{t("kunden.rechnungsadresse")}</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <Feld label={t("adresse.strasse")} breit>
            <Eingabe value={f.billingStreet} onChange={setzen("billingStreet")} />
          </Feld>
          <Feld label={t("adresse.plz")}>
            <Eingabe value={f.billingPostalCode} onChange={setzen("billingPostalCode")} />
          </Feld>
          <Feld label={t("adresse.ort")}>
            <Eingabe value={f.billingCity} onChange={setzen("billingCity")} />
          </Feld>
          <Feld label={t("adresse.land")} hinweis={t("adresse.landHinweis")}>
            <Eingabe maxLength={2} value={f.billingCountry} onChange={setzen("billingCountry")} />
          </Feld>
        </div>

        <div className="mt-8">
          <Feld label={t("kunden.notizen")}>
            <Textfeld value={f.notes} onChange={setzen("notes")} />
          </Feld>
        </div>

        <div className="mt-8 flex gap-3">
          <Knopf type="submit" disabled={laeuft}>{laeuft ? t("aktion.speichert") : t("aktion.speichern")}</Knopf>
          <Knopf type="button" art="neben" onClick={onAbbruch}>{t("aktion.abbrechen")}</Knopf>
        </div>
      </Karte>
    </form>
  );
}

/**
 * Zeigt in der Kundenansicht, wie der Brief tatsaechlich beginnen wird.
 *
 * Die Felder einzeln aufzulisten waere nutzlos: niemand rechnet im Kopf aus,
 * was aus "Herr" plus "Meier" plus "Sie" wird. Der fertige Satz beantwortet
 * die Frage sofort, und ein fehlender Name faellt ebenso sofort auf.
 */
function briefanrede(k: KundeVoll, t: (s: string) => string): string {
  const name = (k.salutation_name ?? "").trim();
  if (k.form_of_address === "du") return name ? `Hallo ${name},` : "Hallo,";
  if (!name || k.salutation === "neutral") return t("kunden.anredeNeutral");
  if (k.salutation === "herr") return `Sehr geehrter Herr ${name},`;
  if (k.salutation === "frau") return `Sehr geehrte Frau ${name},`;
  return `Sehr geehrte Familie ${name},`;
}
